'use client'
import * as React from 'react'
import { fragmentOn } from 'basehub'
import { RichText } from 'basehub/react-rich-text'
import {
  TryLightModeFragment,
  TryLightModeComponent,
} from './_components/try-light-mode'
import {
  FloatingPortal,
  useFloating,
  flip,
  shift,
  inline,
  autoUpdate,
  offset,
} from '@floating-ui/react'
import { Cross2Icon } from '@radix-ui/react-icons'
import { FloatingBannerContext } from './context'

export const FloatingBannerFragment = fragmentOn('AsFloatingBannerComponent', {
  _id: true,
  attachTo: true,
  title: {
    json: { content: true },
  },
  body: {
    json: {
      content: true,
      blocks: {
        __typename: true,
        on_BlockDocument: {
          _id: true,
        },
        on_TryLightModeComponent: TryLightModeFragment,
      },
    },
  },
})

export type FloatingBannerFragment = fragmentOn.infer<
  typeof FloatingBannerFragment
>

export const FloatingBanner = ({
  state,
  data,
  onClose,
}: {
  state: 'open' | 'closed'
  data: FloatingBannerFragment
  onClose: () => void
}) => {
  const [isHovering, setIsHovering] = React.useState(false)
  const timeRemainingRef = React.useRef(10_000)
  const [foundAnchor, setFoundAnchor] = React.useState(false)

  const floating = useFloating({
    placement: 'top',
    open: state === 'open',
    onOpenChange: onClose,
    middleware: [
      inline(),
      flip(),
      shift(),
      offset({ mainAxis: 8, crossAxis: -16 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  React.useEffect(() => {
    if (!data.attachTo) return
    const anchor = document.querySelector<HTMLElement>(data.attachTo)
    if (!anchor) return

    floating.refs.setReference(anchor)
    setFoundAnchor(true)

    function handleClick(e: MouseEvent) {
      if (!anchor) return
      if (e.target instanceof HTMLElement === false) return
      if (e.target === anchor || anchor.contains(e.target)) {
        onClose()
      }
    }

    document.addEventListener('pointerdown', handleClick)
    return () => {
      document.removeEventListener('pointerdown', handleClick)
    }
  }, [data.attachTo, floating.refs, onClose])

  React.useEffect(() => {
    if (isHovering) return

    const now = Date.now()

    const timeout = setTimeout(() => {
      onClose()
    }, timeRemainingRef.current)

    return () => {
      clearTimeout(timeout)
      timeRemainingRef.current = timeRemainingRef.current - (Date.now() - now)
    }
  }, [isHovering, onClose])

  if (!foundAnchor) return null
  return (
    <FloatingPortal>
      <FloatingBannerContext.Provider value={{ onClose }}>
        <div
          ref={floating.refs.setFloating}
          style={{
            ...floating.floatingStyles,
          }}
          className="shadow-modal z-overlay min-w-[160px] max-w-[320px] max-h-[600px] overflow-auto rounded-lg border border-dark-border-solid bg-dark-base px-4 py-3"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <header className="flex justify-between items-center">
            <div className="text-sm text-dark-bright font-medium">
              <RichText content={data.title.json.content} />
            </div>
            <button
              onClick={onClose}
              className="text-dark-muted hover:text-dark-dim cursor-default"
            >
              <Cross2Icon />
            </button>
          </header>
          <div className="text-xs text-dark-muted mt-2 group">
            <RichText
              content={data.body.json.content}
              blocks={data.body.json.blocks}
              components={{ TryLightModeComponent }}
            />
          </div>
        </div>
      </FloatingBannerContext.Provider>
    </FloatingPortal>
  )
}
